import { useRef, useState } from "react";
import { faChartColumn } from "@fortawesome/free-solid-svg-icons";
import {
  Bar, CartesianGrid, ComposedChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { MUTED, NAVY } from "../constants";
import { exportTablePng } from "../exportUtils";
import { formatStateSpendValue } from "../formatters";
import { SectionTitle } from "./SectionTitle";

export function StateSpendChartCard({ chart, isLoading, fallbackTitle, rulesKey }) {
  const sectionRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    if (isLoading) return;
    setIsExporting(true);
    try { await exportTablePng(sectionRef, chart?.title || fallbackTitle); }
    finally { setIsExporting(false); }
  };

  const data = Array.isArray(chart?.data) ? chart.data : [];
  const barSeries = (chart?.series || []).find((s) => s.type === "bar") || { key: "spend_k", label: "Spend $000s" };
  const resolvedRulesKey = rulesKey || (fallbackTitle.includes("From State") ? "freightSpendByFromState" : "freightSpendByToState");

  return (
    <section ref={sectionRef} className="min-w-0 rounded-2xl border border-[#d6dfeb] bg-white shadow-sm overflow-hidden flex flex-col">
      <div className="px-4 pt-4">
        <SectionTitle title={chart?.title || fallbackTitle} icon={faChartColumn} rulesKey={resolvedRulesKey} onExport={handleExport} isExporting={isExporting} disabled={isLoading} />
        {chart?.subtitle && (
          <p className="text-xs mt-1" style={{ color: MUTED }}>{chart.subtitle}</p>
        )}
      </div>
      <div className="px-3 pt-3 pb-4" style={{ height: 280 }}>
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-sm" style={{ color: MUTED }}>Loading…</div>
        ) : !data.length ? (
          <div className="h-full flex items-center justify-center text-sm" style={{ color: MUTED }}>No data available.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                minTickGap={12}
              />
              <YAxis tick={{ fontSize: 10, fill: MUTED }} tickFormatter={formatStateSpendValue} axisLine={false} tickLine={false} width={48} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #d6dfeb" }}
                formatter={(value) => formatStateSpendValue(value)}
              />
              <Bar dataKey={barSeries.key} name={barSeries.label} fill={NAVY} radius={[4, 4, 0, 0]}>
                {barSeries.show_value_labels !== false && (
                  <LabelList dataKey={barSeries.key} position="top" formatter={formatStateSpendValue} style={{ fill: NAVY, fontSize: 10, fontWeight: 700 }} />
                )}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
